import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { CustomWindow, FeatureService, LoggingService, StatefulService, WINDOW } from '@woolworthsnz/styleguide';
import { ContextResponse, ProductResponse } from '@woolworthsnz/trader-api';
import { CitrusAdState } from './citrus-ad.state';
import { take } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
	providedIn: 'root',
})
export class CitrusAdService extends StatefulService<CitrusAdState> {
	private enableCitrusLogging = false;

	constructor(
		@Inject(PLATFORM_ID) private platform: any,
		@Inject(WINDOW) private window: CustomWindow,
		private loggingService: LoggingService,
		private featureService: FeatureService,
		private httpClient: HttpClient
	) {
		super(new CitrusAdState());
	}

	reportClick({ sku, adId }: { sku?: string; adId?: string }): void {
		if (!adId) {
			return;
		}

		if (isDevMode() && this.enableCitrusLogging) {
			this.loggingService.trackEvent('[CitrusAdService]', { event: `reportClick called for adId: ${adId}` });
		}

		this.callCitrusAd(adId, false);
		this.setState(<CitrusAdState>{ sku: sku, adId: adId });
	}

	reportImpressions(products: ProductResponse[]): void {
		if (!products || !products.length) {
			return;
		}

		if (isDevMode() && this.enableCitrusLogging) {
			this.loggingService.trackEvent('[CitrusAdService]', { event: 'reportImpressions called' });
		}

		// filter products with ad Ids
		const productsWithAdId = products.filter((product) => product.adId);

		productsWithAdId.forEach((product) => {
			if (product.adId) {
				this.callCitrusAd(product.adId, true);
			}
		});
	}

	clearState(): void {
		this.setState(<CitrusAdState>{ sku: '', adId: '' });
	}

	private callCitrusAd(adId: string, callingImpression = true): void {
		if (!isPlatformBrowser(this.platform) || !this.window['citrusAd']) {
			return;
		}

		this.featureService
			.isEnabled(ContextResponse.EnabledFeaturesEnum.EnableCallingCitrusWithFetch)
			.pipe(take(1))
			.subscribe((isEnabled) => {
				if (isEnabled) {
					this.callCitrusWithFetch(adId, callingImpression);
				} else {
					this.callingCitrusWithLibrary(adId, callingImpression);
				}
			});
	}

	private callCitrusWithFetch(adId: string, callingImpression = true): void {
		const subUrl = callingImpression ? 'first-i' : 'second-c';

		this.httpClient
			.get(`${this.window['citrusAd'].api.basePath}/resource/${subUrl}/${adId}`)
			.pipe(take(1))
			.subscribe({
				next: this.handleFetchSuccess,
				error: (error) => {
					this.handleFetchError(error, callingImpression);
				},
			});
	}

	private handleFetchSuccess(result: any): void {
		if (isDevMode() && this.enableCitrusLogging) {
			this.loggingService.trackEvent(JSON.stringify(result), {});
		}
	}

	private handleFetchError(e: Error, callingImpression = true): void {
		e.name += callingImpression ? ' CitrusAdImpressionError' : ' CitrusAdClickError';
		e.message += callingImpression ? ' CitrusAd impression failed' : ' CitrusAd click failed';

		this.loggingService.trackException({ error: e });
	}

	private callingCitrusWithLibrary(adId: string, callingImpression = true): void {
		if (callingImpression) {
			this.window['citrusAd']
				.reportImpression(adId)
				.then((result: any) => {
					if (isDevMode() && this.enableCitrusLogging) {
						this.loggingService.trackEvent(JSON.stringify(result), {});
					}
				})
				.catch((e: any) => {
					this.loggingService.trackException({ error: e });
				});
			return;
		}

		this.window['citrusAd']
			.reportClick(adId)
			.then((result: any) => {
				if (isDevMode() && this.enableCitrusLogging) {
					this.loggingService.trackEvent(JSON.stringify(result), {});
				}
			})
			.catch((e: any) => {
				this.loggingService.trackException({ error: e });
			});
	}
}
